import { components } from '@doltech/pageManagement/dto';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { DOLTypographyVariant, Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { TextWithLinkPdf } from '@doltech/ui/lib/figma/Layout/Landing/Content/TextWithLinkPdf';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { PageTypeEnum } from '@doltech/utils/lib/constants';
import { usePdfTrackingParams } from '@doltech/utils/lib/pdf';
import { getLandingUrlFromPath, mergeUrlWithParams } from '@doltech/utils/lib/url';
import * as React from 'react';
import styled from 'styled-components';

import doTestIcon from './assets/do-test-icon.png';
import viewDeIcon from './assets/view-de-icon.png';
import viewSolutionIcon from './assets/view-solution-icon.png';
import viewVocabIcon from './assets/view-vocab-icon.png';
import { ImageIcon } from './ImageIcon';

const ActionLinkMain = styled.div.withConfig({
  componentId: 'HeaderSEOPDF_ActionLinkMain',
} as any)`
  &.action-links {
    display: flex;
    flex-direction: column;
    align-items: start;
    pointer-events: initial;

    .action {
      display: flex;
      align-items: center;
      font-size: 24px;
      padding-right: 24px;
      color: ${colorsV2.black100};

      &:hover {
        span {
          text-decoration: underline;
        }
      }

      &-icon {
        font-size: 24px;
        margin-right: 8px;

        @media screen and ${deviceBreakpoints.mobile} {
          margin-right: 0;
          margin-bottom: 4px;
        }
      }

      &-view-explanation {
        .action-icon {
          color: ${colorsV2.green100};
        }
      }

      &-view-vocab {
        .action-icon {
          color: ${colorsV2.yellow100};
        }
      }

      &-view-transcript {
        .action-icon {
          color: ${colorsV2.purple100};
        }
      }

      &-download-test {
        .action-icon {
          color: ${colorsV2.blue100};
        }
      }
    }

    button {
      background-color: ${colorsV2.white100};
      font-size: 12px;
      line-height: 16px;
      color: ${colorsV2.black100};
      font-weight: 500;
    }
  }
`;

interface ActionLinksProps {
  seoData: components['schemas']['PageResponseDTOObject'];
  seoPageData: components['schemas']['SEOPageDTO'][];
  textSizeVariant?: DOLTypographyVariant;
}

export const ActionLinksPdf = (props: ActionLinksProps) => {
  const { seoPageData = [], seoData, textSizeVariant = 'medium/14' } = props;

  const pdfTrackingParams = usePdfTrackingParams();

  const actionDoTest = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.DO_TEST &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.DO_TEST),
    [seoPageData, seoData.type]
  );

  const actionViewTestQuestion = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.VIEW_TEST_QUESTION &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.VIEW_TEST_QUESTION),
    [seoPageData, seoData.type]
  );

  const actionViewVocab = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.VIEW_VOCAB &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.VIEW_VOCAB),
    [seoPageData, seoData.type]
  );
  const actionViewSolution = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.VIEW_SOLUTION &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.VIEW_SOLUTION),
    [seoPageData, seoData.type]
  );

  return (
    <ActionLinkMain className="action-links">
      {actionDoTest?.url && (
        <div className="action action-do-test">
          <TextWithLinkPdf
            link={mergeUrlWithParams(getLandingUrlFromPath(actionDoTest.url), pdfTrackingParams)}
            variant={textSizeVariant}
            content={
              <>
                <ImageIcon alt="Do test" src={doTestIcon} className="action-icon" />
                <Typography.Text variant={textSizeVariant}>Làm Bài</Typography.Text>
              </>
            }
          />
        </div>
      )}
      {actionViewTestQuestion?.url && (
        <div className="action action-do-test">
          <TextWithLinkPdf
            link={mergeUrlWithParams(
              getLandingUrlFromPath(actionViewTestQuestion.url),
              pdfTrackingParams
            )}
            variant={textSizeVariant}
            content={
              <>
                <ImageIcon alt="View test question" src={viewDeIcon} className="action-icon" />
                <Typography.Text variant={textSizeVariant}>Đề và đáp án</Typography.Text>
              </>
            }
          />
        </div>
      )}
      {actionViewSolution?.url && (
        <div className="action action-do-test">
          <TextWithLinkPdf
            link={mergeUrlWithParams(
              getLandingUrlFromPath(actionViewSolution.url),
              pdfTrackingParams
            )}
            variant={textSizeVariant}
            content={
              <>
                <ImageIcon alt="View solution" src={viewSolutionIcon} className="action-icon" />
                <Typography.Text variant={textSizeVariant}>Xem giải thích</Typography.Text>
              </>
            }
          />
        </div>
      )}
      {actionViewVocab?.url && (
        <div className="action action-do-test">
          <TextWithLinkPdf
            link={mergeUrlWithParams(getLandingUrlFromPath(actionViewVocab.url), pdfTrackingParams)}
            variant={textSizeVariant}
            content={
              <>
                <ImageIcon alt="View vocabulary" src={viewVocabIcon} className="action-icon" />
                <Typography.Text variant={textSizeVariant}>Từ vựng</Typography.Text>
              </>
            }
          />
        </div>
      )}
    </ActionLinkMain>
  );
};
